/* eslint-disable camelcase */
import { FastField, Formik, FormikProps } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import { Actionsheet, IRadioGroupProps, Radio, useDisclose, View } from "native-base"
import * as React from "react"
import { Appearance, Dimensions, ImageBackground, ImageStyle, Linking, StyleProp, TextInput, TouchableOpacity, ViewStyle } from "react-native"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { color, nativeBaseTheme } from "../../../theme"
import { TextField } from "../../field/text-field/text-field"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { NumeralInput, NumeralInputProps } from "../../input/numeral-input/numeral-input"
import { Picker } from "../../picker/picker"
import { FormProps } from "../form"
import { useCallback } from "react"
import { useStores } from "../../../models"
import { createAxios } from "../../../utils/axios"
import { Button } from "../../button/button"
import { Text } from "../../text/text"
import Icon from 'react-native-vector-icons/MaterialIcons';
import numeral from 'numeral'
import { InputProps } from "../../input/input"
import { ActionsheetIosAware } from "../../quick-input-actionsheet/quick-input-actionsheet"

const BarunoImage = require("./baruno.png");
const windowsWidth = Dimensions.get("window").width - 32;

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const FLEX_DIRECTION: ViewStyle = {
  flexDirection: 'row',
}

const FLEX: ViewStyle = {
  flex: 1,
  ...FLEX_DIRECTION
}

const BUTTON_BACK_MODAL: ViewStyle = {
  borderRadius: 4,
  paddingVertical: 10,
  marginTop: 22
}

const IMAGE_BG: ImageStyle = {
  position: "relative",
  width: windowsWidth,
  height: 168,

}

const IMAGE: ImageStyle = {
  // resizeMode: "contain",
  width: '100%',
  height: 168,
  borderRadius: 16
}

const tray = [{id: 1, name: '1' }, {id: 2, name: '2' }, {id: 3, name: '3' }, {id: 4, name: '4' }]

export interface PondProfileFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  style?: StyleProp<ViewStyle>
  initialValues?: {
    farm_id?: number
    pond_id?: number
    rfid?: string
    name?: string
    area?: string
    type?: string
    length?: number
    width?: number
  }
}


const NumeralField = (
  { name, label, input = {}, nextInput = {}, colorScheme, formControlProps, numeralInputProps }:
  { name: string, label?: string, input: any, nextInput: any, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, numeralInputProps?: NumeralInputProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, handleBlur }, meta }) => (
      <FormControl
        isRequired={name!=="area"}
        isInvalid={meta.touched && !!meta.error}
        labelPreset="title3"
        labelColor={color.palette.darkGrey}
        labelTx={`pondProfileForm.${_.camelCase((label||field.name))}` as TxKeyPath}
        colorScheme={colorScheme}
        isDisabled={name==="area"}
        inputProps={{
          testID: `jala:id/${field.name}-input`,
          autoCapitalize: "none",
          autoComplete: "off",
          autoCorrect: false,
          textContentType: "none",
          keyboardType: "numeric",
          returnKeyType: nextInput[field.name] ? "next" : "go",
          onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : null,
          placeholderTx: `pondProfileForm.${_.camelCase((label||field.name))}Placeholder` as TxKeyPath,
          defaultValue: field.value,
          onBlur: handleBlur(field.name),
        }}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        {(inputProps) => (
          <NumeralInput ref={input[field.name]} colorScheme={colorScheme}
            onChangeNumber={(value) => setFieldValue(field.name, value)} {...inputProps} {...numeralInputProps} />
        )}
      </FormControl>
    )}
  </FastField>
)

const PickerField = (
  { name, colorScheme, formControlProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => {
      return (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelPreset="title3"
        labelColor={color.palette.darkGrey}
        labelTx={`pondProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <Picker
          data={tray}
          colorScheme={colorScheme}
          placeholderTx={`pondProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
          }}
          isUseSearch={false}
          titleTx="pondProfileForm.feedingTrayCountPlaceholder"
        />
      </FormControl>
    )}}
  </FastField>
)

const RadioField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, radioGroupProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, radioGroupProps?: IRadioGroupProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelPreset="title3"
        labelTx={`pondProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <Radio.Group
          name={name}
          value={field.value}
          onChange={async (value) => {
            await setFieldValue(field.name, value || null);
            await setFieldTouched(field.name)
          }}
          style={FLEX_DIRECTION}
          {...radioGroupProps}
        >
          <View style={FLEX}>
            <Radio value="square" size="sm">{_.startCase(translate('pondProfileForm.square'))}</Radio>
          </View>
          <View style={FLEX}>
            <Radio value="circle" size="sm" ml="8px">{_.startCase(translate('pondProfileForm.circle'))}</Radio>
          </View>
        </Radio.Group>
      </FormControl>
    )}
  </FastField>
)

const BarunoModal: React.FC<{ colorScheme?: "light" | "dark" }> = observer(function LogoutModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), children } = props;
  const { isOpen, onClose, onOpen } = useDisclose()

  const handlePress = () => Linking.openURL(`https://jala.tech/id/baruno`).catch()

  return (<>
    <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <ActionsheetIosAware isOpen={isOpen} onClose={onClose}>
      <Actionsheet.Content alignItems="stretch" px={0} py={0}>
        <View
          padding="16px"
          backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
        >
          <View marginBottom="16px">
            {/* <View height="4px" width="56px" alignSelf="center" mb="20px" borderRadius="8px" backgroundColor={color.palette.black} /> */}
            <Text preset="display3" mb="8px" color={color.palette.gray800} tx="pondProfileForm.rfidTitle" textAlign="center" />
            <Text preset="body2" mb="24px" color={color.palette.gray800} tx="pondProfileForm.rfidSubtitle" textAlign="center" />
            <ImageBackground source={BarunoImage} imageStyle={IMAGE} style={IMAGE_BG}>
              <View position="absolute" top={146} flexDirection="row" borderRadius={16} alignItems="center" px="13px">
                <Text preset="body3" tx="pondProfileForm.visit" color={color.palette.gray800} colorScheme={colorScheme} />
                <Button preset="link" textPreset="body3" onPress={handlePress} text={` jala.tech/id/baruno`} colorScheme={colorScheme} />
              </View>
            </ImageBackground>
          </View>
          <Button
            testID="jala:id/back-button"
            tx="profileScreen.logoutButtonCancel"
            colorScheme={colorScheme}
            style={BUTTON_BACK_MODAL}
            onPress={onClose}
          />
        </View>
      </Actionsheet.Content>
    </ActionsheetIosAware>
  </>)
})

const FormikField: React.FC<{ colorScheme?: "light"|"dark" } & FormikProps<any>> = observer(function FormikField(props) {
  const { colorScheme, values, setFieldValue, setFieldTouched } = props;

  const input = {
    name: React.useRef<TextInput>(),
    rfid: React.useRef<TextInput>(),
    length: React.useRef<TextInput>(),
    width: React.useRef<TextInput>(),
    deep: React.useRef<TextInput>(),
  }

  const nextInput = {
    name: () => input.rfid.current && input.rfid.current.focus(),
    rfid: () => input.length.current && input.length.current.focus(),
    length: () => input.width.current && input.width.current.focus(),
    width: () => input.deep.current && input.deep.current.focus(),
  }

  const areaInputProps = React.useMemo<InputProps>(() => ({
    testID: `jala:id/${values.name}.area-input`,
    placeholderTx: "pondForm.areaPlaceholder",
    value: values.type === 'circle'
      ? numeral(Math.PI * Math.pow(values.length, 2)/4).format('0,0.[0]')
      : numeral(values.length * values.width).format('0,0.[0]'),
    style: {
      borderWidth: 0
    }
  }), [values.length, values.name, values.type, values.width])

  return (
    <>
      <TextField
        name="name"
        txPrefix="pondProfileForm"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ isRequired: true, labelPreset: "title3" }}
      />
      <TextField
        name="rfid"
        txPrefix="pondProfileForm"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{
          labelComponent: <BarunoModal><View flexDirection="row" alignItems="center">
              <Text preset="title4" tx="pondProfileForm.rfid" colorScheme={colorScheme} mr="3px" />
              <Icon name="info-outline" size={18} color={color.palette.gray800} />
            </View>
          </BarunoModal>
        }}
      />
      <RadioField name="type"
        radioGroupProps={{
          name: "type",
          value: values.type !== null ? values.type : 'square',
          onChange: async (value) => {
            await setFieldValue("type", value !== null && value !== undefined ? value : null);
            await setFieldTouched("type")
          }
        }}
      />
      {values.type !== 'circle' ? (
        <View style={FLEX_DIRECTION}>
          <NumeralField name="length" colorScheme={colorScheme} input={input} nextInput={nextInput} formControlProps={{ flex: 1 }} />
          <NumeralField name="width" colorScheme={colorScheme} input={input} nextInput={nextInput} formControlProps={{ flex: 1 }} />
        </View>
      ): <NumeralField label="diameter" name="length" colorScheme={colorScheme} input={input} nextInput={nextInput} />}
      <View style={FLEX_DIRECTION}>
        <FormControl
          labelTx="pondProfileForm.area"
          colorScheme={colorScheme}
          inputProps={areaInputProps}
          flex={1}
          style={FORM_DROPDOWN}
        />
        <NumeralField name="deep" colorScheme={colorScheme} input={input} nextInput={nextInput} formControlProps={{ flex: 1 }} />
      </View>
      {/* <DateField name="created_at" colorScheme={colorScheme} formControlProps={{ isRequired: true, isDisabled: true }} /> */}
      <PickerField name="feeding_tray_count" colorScheme={colorScheme} formControlProps={{ isRequired: true }} />
      <View flexDirection="row" my="10px" px="16px" py="12px" alignItems="flex-start" borderTopWidth={0.5} borderColor={color.palette.lightGrey} >
        <Icon name="info-outline" size={20} color={color.palette.grey} />
        <View flex={2}>
          <Text preset="body2" color={color.palette.grey} ml="10px" tx="pondProfileForm.editPondDesc" numberOfLines={4} />
        </View>
      </View>
    </>
  )
})


/**
 * Describe your component here
 */
export const PondProfileForm = observer(function PondProfileForm(props: PondProfileFormProps) {
  const { initialValues: initialValuesProps = {}, onSubmit: onSubmitProps, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props

  const { appStore: { farmStore } } = useStores()

  const initialValues = React.useMemo<PondProfileFormProps['initialValues']>(() => {
    const area = initialValuesProps.type === 'circle'
      ? numeral(Math.PI * Math.pow(initialValuesProps.length, 2)/4).format('0,0.[0]')
      : numeral(initialValuesProps.length * initialValuesProps.width).format('0,0.[0]')

    return {
      farm_id: null,
      name: '',
      rfid: null,
      area: area || null,
      ...initialValuesProps,
      type: initialValuesProps.type === 'circle' ? 'circle' : 'square'
    }
  }, [initialValuesProps])

  const names = React.useMemo(() => {
    const data = [...(farmStore && farmStore.ponds.filter(p => p.name !== initialValues.name))]
    return data.map(p => p.name)
  }, [farmStore, initialValues.name])

  const validationSchema = React.useMemo(() => yup.object({
    name: yup.string().ascii().notOneOf([...names], translate('yup.array.unique')).nullable().required().label(translate("pondForm.name")),
    rfid: yup.string().length(8).test(function (value) {
        return new Promise((resolve) => {
          if (!value || (value || '').length !== 8) return resolve(true)
          if (initialValuesProps.rfid === value) return resolve(true)
          createAxios().get(`/rfids?doesnt_have=pond&search=${value}`, {}).then((res) => {
            const { data: { data } } = res
            resolve(!!data.length)
          }).catch((e) => {
            if (e.message === 'Network Error')
              resolve(this.createError({
                path: 'rfid',
                message: translate('Network Error')
              }))
            else
              resolve(this.createError({
                path: 'rfid',
                message: translate("pondProfileForm.rfidNotFound")
              }))
          })
        })
      }).nullable().label(translate("pondProfileForm.rfidLabel")),
    type: yup.string().oneOf(['square', 'circle']).nullable().label(translate('pondForm.type')),
    length: yup.number().min(0.5).max(500).nullable().required().when('type', {
      is: 'circle',
      then: (schema) => schema.label(translate("pondForm.diameter")),
      otherwise: (schema) => schema.label(translate("pondForm.length"))
    }),
    width: yup.number().min(0.5).max(500).when('type', {
      is: 'circle',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable().required()
    }).label(translate("pondForm.width")),
    deep: yup.number().min(0.5).max(10).nullable().required().label(translate("pondForm.deep")),
  }),[initialValuesProps.rfid, names])

  const onSubmit = useCallback((values) => {
    const model = _.omit(values, ['area', 'pro'])

    onSubmitProps(model)
  }, [onSubmitProps])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {(formikProps) => (
        <FormikField colorScheme={colorScheme} {...formikProps} />
      )}
    </Formik>
  )
})

PondProfileForm.displayName = 'PondProfileForm'
